/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/evidence/ClaimEvidencePanel.tsx
 * FILE_ID: QUOTE-CLAIMS-EVIDENCE-PANEL-001
 * PURPOSE: Assemble the claim evidence manifest and canonical source pickers.
 */

import { useMemo, useRef, useState } from 'react'
import { HardDrive, Loader2, Mail, Paperclip, Upload } from 'lucide-react'
import {
  QDriveFilePicker,
  type QDrivePickerFile,
} from '@quotech/quote-builder'

import { EmailEvidencePicker } from './EmailEvidencePicker'
import { EvidenceRow } from './EvidenceRow'
import { moveEvidenceId } from './manifest'
import type { ClaimEvidenceState } from './types'

interface ClaimEvidencePanelProps {
  editable: boolean
  evidence: ClaimEvidenceState
  lines?: Array<{ id: string; description: string }>
}

function errorMessage(error: unknown): string {
  const detail = (
    error as { response?: { data?: { detail?: string } } }
  )?.response?.data?.detail
  return detail || (error instanceof Error ? error.message : 'Evidence update failed.')
}

export function ClaimEvidencePanel({
  editable,
  evidence,
  lines = [],
}: ClaimEvidencePanelProps) {
  const [qdriveOpen, setQdriveOpen] = useState(false)
  const [emailOpen, setEmailOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [selectedLineId, setSelectedLineId] = useState('')
  const uploadInput = useRef<HTMLInputElement>(null)
  const emailIds = useMemo(
    () => new Set(
      evidence.items.flatMap((item) => item.email ? [item.email] : []),
    ),
    [evidence.items],
  )

  const run = async (action: () => Promise<void>) => {
    setActionError(null)
    try {
      await action()
    } catch (error) {
      setActionError(errorMessage(error))
      throw error
    }
  }

  const addQDriveFile = async (file: QDrivePickerFile) => {
    if (evidence.items.some((item) => item.file === file.id)) {
      throw new Error('This QDrive file is already in the evidence manifest.')
    }
    await run(() => evidence.addFile(file.id, selectedLineId || null))
  }

  const uploadFile = async (file: File) => {
    if (file.size > 20 * 1024 * 1024) {
      setActionError('Evidence files must not exceed 20 MB.')
      return
    }
    await run(() => evidence.uploadFile(file, selectedLineId || null))
  }

  const move = async (index: number, direction: -1 | 1) => {
    await run(() => evidence.reorder(
      moveEvidenceId(evidence.items, index, direction),
    ))
  }

  return (
    <section
      id="evidence"
      className="scroll-mt-24 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
            <Paperclip className="h-4 w-4" />
            Evidence
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Order, caption and choose what appears in the document or send.
          </p>
        </div>
        {editable && (
          <div className="flex flex-wrap justify-end gap-2">
            {lines.length > 0 && <label className="text-xs text-slate-500">Attach new evidence to<select value={selectedLineId} onChange={(event) => setSelectedLineId(event.target.value)} className="ml-2 min-h-8 rounded-md border px-2 dark:bg-slate-900"><option value="">Whole claim</option>{lines.map((line) => <option key={line.id} value={line.id}>{line.description}</option>)}</select></label>}
            <input
              ref={uploadInput}
              type="file"
              className="hidden"
              aria-label="Upload evidence file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.txt"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) void uploadFile(file).catch(() => undefined)
                event.target.value = ''
              }}
            />
            <button
              type="button"
              onClick={() => uploadInput.current?.click()}
              disabled={evidence.isMutating}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Upload className="h-3.5 w-3.5" />
              Upload
            </button>
            <button
              type="button"
              onClick={() => setQdriveOpen(true)}
              disabled={evidence.isMutating}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <HardDrive className="h-3.5 w-3.5" />
              QDrive
            </button>
            <button
              type="button"
              onClick={() => setEmailOpen(true)}
              disabled={evidence.isMutating}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Mail className="h-3.5 w-3.5" />
              Email
            </button>
          </div>
        )}
      </div>

      {actionError && (
        <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {actionError}
        </p>
      )}
      {evidence.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading evidence...
        </div>
      ) : evidence.error ? (
        <p className="py-8 text-center text-sm text-rose-600">
          Evidence could not be loaded.
        </p>
      ) : evidence.items.length === 0 ? (
        <div className="py-8 text-center">
          <Paperclip className="mx-auto h-7 w-7 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-500">
            No evidence added
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Upload a file, choose from QDrive, or add a mailbox message.
          </p>
        </div>
      ) : (
        <ul className="mt-4 space-y-3">
          {evidence.items.map((item, index) => (
            <EvidenceRow
              key={item.id}
              item={item}
              index={index}
              total={evidence.items.length}
              editable={editable}
              disabled={evidence.isMutating}
              onUpdate={(values) => run(() => evidence.update(item.id, values))}
              onRemove={() => run(() => evidence.remove(item.id))}
              onMove={(direction) => move(index, direction)}
              lines={lines}
            />
          ))}
        </ul>
      )}

      {!editable && evidence.items.length > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          This issued document's evidence manifest is locked.
        </p>
      )}

      <QDriveFilePicker
        open={qdriveOpen}
        title="Add QDrive evidence"
        description="Choose an organisation file for this document."
        onClose={() => setQdriveOpen(false)}
        onSelect={addQDriveFile}
      />
      <EmailEvidencePicker
        open={emailOpen}
        excludedIds={emailIds}
        onClose={() => setEmailOpen(false)}
        onSelect={async (email) => run(() => evidence.addEmail(email.id, selectedLineId || null))}
      />
    </section>
  )
}
